import React, { createContext, useState, useCallback, ReactNode } from 'react';

/**
 * Represents a single navigation item
 */
export interface NavigationItem {
  /** Display name of the navigation item */
  name: string;
  /** Route path (e.g., '/okr/objectives') */
  href: string;
  /** Heroicon component for visual identifier */
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  /** Badge content (number or text) for notifications/counts */
  badge?: number | string;
  /** Required user roles to view this item */
  requiredRoles?: string[];
  /** Exact match for route highlighting (default: false) */
  exact?: boolean;
  /** Child navigation items for nested menus (future) */
  children?: NavigationItem[];
}

/**
 * Represents the complete navigation configuration for a module
 */
export interface ModuleNavigation {
  /** Unique module identifier (e.g., 'okr', 'collaborators') */
  moduleId: string;
  /** List of navigation items for this module */
  items: NavigationItem[];
  /** Whether this module's navigation is currently active */
  isActive: boolean;
  /** Optional display name for the module (for aria-label) */
  displayName?: string;
}

/**
 * Context value for managing module navigation state
 */
export interface ModuleNavigationContextValue {
  /** Current active module navigation (null if no module active) */
  moduleNav: ModuleNavigation | null;
  /** Function to set/update module navigation */
  setModuleNav: (nav: ModuleNavigation | null) => void;
}

/**
 * Context for module navigation state management
 */
export const ModuleNavigationContext = createContext<ModuleNavigationContextValue | null>(null);

/**
 * Provider component for module navigation context
 *
 * @example
 * ```tsx
 * <ModuleNavigationProvider>
 *   <Layout />
 * </ModuleNavigationProvider>
 * ```
 */
export const ModuleNavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [moduleNav, setModuleNav] = useState<ModuleNavigation | null>(null);

  const contextValue: ModuleNavigationContextValue = {
    moduleNav,
    setModuleNav: useCallback((nav: ModuleNavigation | null) => {
      setModuleNav(nav);
    }, [])
  };

  return (
    <ModuleNavigationContext.Provider value={contextValue}>
      {children}
    </ModuleNavigationContext.Provider>
  );
};
